import { Typography } from '@mui/material'
import { useFormContext } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

import { GapBox } from 'components/Box'
import { BoxForm, RadioGroup, Switch, TextField } from 'components/Form'
import { SectionHeader } from 'components/Header'
import Paper from 'components/Paper'
import { TooltipProps } from 'components/Tooltip'
import { useMatchTypeRadioGroup } from 'features/inventory/page/forms/GeneralForm/hooks/useMatchTypeRadioGroup'
import { useAbility } from 'providers/casl'
import { CONTAINER_SPACING } from 'providers/material-ui/theme/constants'
import { Schema } from './schema'

export const TriggersSection = (): JSX.Element => {
  const { t } = useTranslation('features/domain', {
    keyPrefix: 'form.richPlacements.multiAnchor.triggers',
  })
  const { control, watch } = useFormContext<Schema>()
  const ability = useAbility()

  const matchTypeTooltip: TooltipProps = {
    content: [
      t('matchType.tooltip.line1'),
      t('matchType.tooltip.line2'),
      t('matchType.tooltip.line3'),
      t('matchType.tooltip.line4'),
    ],
  }

  const matchTypeOptions = useMatchTypeRadioGroup()

  const isDisabled: boolean = ability.cannot('update', 'Domain')

  const { customVariableTriggerEnabled, pathTriggerEnabled } = watch()

  return (
    <Paper data-cy='section-triggers'>
      <SectionHeader
        subtitle={
          <Trans
            components={{
              bold: (
                <Typography
                  component='span'
                  fontWeight='bold'
                  variant='inherit'
                />
              ),
            }}
            i18nKey='form.richPlacements.multiAnchor.triggers.subtitle'
            ns='features/domain'
          />
        }
        title={t('title')}
      />

      <GapBox />

      <BoxForm>
        <Switch
          control={control}
          disabled={isDisabled}
          label={t('pathTriggerEnabled.label')}
          labelPlacement='start'
          name='pathTriggerEnabled'
          tooltip={{
            content: t('pathTriggerEnabled.tooltip'),
          }}
        />

        {pathTriggerEnabled && (
          <BoxForm sx={{ ml: CONTAINER_SPACING }}>
            <TextField
              control={control}
              disabled={isDisabled}
              label={t('path.label')}
              name='path'
              placeholder={t('path.placeholder')}
              tooltip={{
                content: t('path.tooltip'),
              }}
            />

            <RadioGroup
              control={control}
              disabled={isDisabled}
              label={t('matchType.name')}
              name='matchType'
              options={matchTypeOptions}
              tooltip={matchTypeTooltip}
            />
          </BoxForm>
        )}
      </BoxForm>

      <GapBox />

      <BoxForm>
        <Switch
          control={control}
          disabled={isDisabled}
          label={t('customVariableTriggerEnabled.label')}
          labelPlacement='start'
          name='customVariableTriggerEnabled'
          tooltip={{
            content: t('customVariableTriggerEnabled.tooltip'),
          }}
        />

        {customVariableTriggerEnabled && (
          <BoxForm sx={{ ml: CONTAINER_SPACING }}>
            <TextField
              control={control}
              disabled={isDisabled}
              label={t('customVariableName.label')}
              name='customVariableName'
              placeholder={t('customVariableName.placeholder')}
            />

            <TextField
              control={control}
              disabled={isDisabled}
              label={t('customVariableValue.label')}
              name='customVariableValue'
              placeholder={t('customVariableValue.placeholder')}
            />
          </BoxForm>
        )}
      </BoxForm>
    </Paper>
  )
}
